import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  runTransaction,
  serverTimestamp,
  writeBatch,
  Transaction
} from 'firebase/firestore';
import { db } from './firebase';
import { UnifiedInventoryTransactionService } from './unifiedInventoryTransactions';
import { SalesDetail, SalesHeader, Counter } from '../types';
import { InventoryJournal } from '../types/inventory';

// Counter management functions
export const getNextCounter = async (counterId: 'transactionId' | 'transactionDetailId' | 'productId'): Promise<number> => {
  return await runTransaction(db, async (transaction) => {
    const counterRef = doc(db, 'counters', counterId);
    const counterDoc = await transaction.get(counterRef);
    
    if (!counterDoc.exists()) {
      // Initialize counter if it doesn't exist
      transaction.set(counterRef, { id: counterId, value: 1 });
      return 1;
    }
    
    const currentValue = counterDoc.data().value;
    const nextValue = currentValue + 1;
    
    transaction.update(counterRef, { value: nextValue });
    return nextValue;
  });
};

export const getNextTransactionId = async (): Promise<string> => {
  const nextNumber = await getNextCounter('transactionId');
  return `INV-${nextNumber.toString().padStart(3, '0')}`;
};

export const getNextTransactionDetailId = async (): Promise<number> => {
  return await getNextCounter('transactionDetailId');
};

export const getNextProductId = async (): Promise<string> => {
  const nextNumber = await getNextCounter('productId');
  return `FG${nextNumber.toString().padStart(3, '0')}`;
};

// Daily seeding functions - SIMPLIFIED for testing
export const seedDailySalesDetails = async (
  forDate: string,
  warehouseId: string = 'default-warehouse',
  createdBy: string = 'system',
  warehousePricing?: { [productId: string]: number }
): Promise<{ success: boolean; message: string; count?: number; recordsCreated?: number }> => {
  try {
    console.log(`🔍 Checking for existing sales data for ${forDate}...`);
    
    // Check if seeding already exists for this date
    const existingSeeding = await getDocs(
      query(
        collection(db, 'salesDetails'),
        where('date', '==', forDate), // Fixed: use 'date' not 'forDate'
        limit(1)
      )
    );

    if (!existingSeeding.empty) {
      return {
        success: false,
        message: `Sales data already exists for ${forDate}. Use a different date or clear existing data first.`
      };
    }

    console.log('📊 Loading outlets and products (simplified approach)...');

    // SIMPLIFIED: Get ALL active outlets - no route/locality filtering
    const outletsQuery = query(
      collection(db, 'outlets'),
      where('status', '==', 'active')
    );
    const outletsSnapshot = await getDocs(outletsQuery);
    console.log(`Found ${outletsSnapshot.docs.length} active outlets`);

    // Get ALL active products - no warehouse filtering
    const productsQuery = query(
      collection(db, 'products'),
      where('isActive', '==', true)
    );
    const productsSnapshot = await getDocs(productsQuery);
    console.log(`Found ${productsSnapshot.docs.length} active products`);

    if (outletsSnapshot.docs.length === 0 || productsSnapshot.docs.length === 0) {
      return {
        success: false,
        message: `Insufficient data: ${outletsSnapshot.docs.length} outlets, ${productsSnapshot.docs.length} products found`,
        recordsCreated: 0
      };
    }

    // Create batch for bulk operations
    const batch = writeBatch(db);
    let recordsCreated = 0;

    console.log('🏭 Creating sales detail records for all outlets...');

    // Generate sales details for each outlet × product combination
    for (const outletDoc of outletsSnapshot.docs) {
      const outlet = { id: outletDoc.id, ...outletDoc.data() } as any;

      for (const productDoc of productsSnapshot.docs) {
        const product = { id: productDoc.id, ...productDoc.data() } as any;
        
        // Use random values for recommended shelf qty (5-25 units)
        const shelfQtyReco = Math.floor(Math.random() * 20) + 5;

        const transactionDetailId = await getNextTransactionDetailId();
        
        // 🔧 FIX: Use standardized productId field instead of document ID
        const productId = product.productId || product.id;
        const productName = product.name || product.productName || `Product ${productId}`;
        // Use warehouse pricing if available, otherwise use 0 (NO FALLBACK as per requirement)
        const unitRate = warehousePricing?.[productId] || 0;

        const salesDetail: Omit<SalesDetail, 'id'> = {
          transactionDetailId,
          productId: productId, // ✅ Fixed: Use productId field for consistency
          productName: productName,
          outletId: outlet.id,
          date: forDate,
          shelfQtyReco, // Target shelf quantity (recommended)
          openingStock: 0, // Initially 0 - to be entered by salesman
          replaceQty: 0, // Initially 0 - to be entered by salesman
          saleQty: 0, // Initially 0 - to be calculated/entered by salesman
          unitRate: unitRate, // Use warehouse pricing or 0
          lineTotal: 0, // Will be calculated when saleQty is entered
          taxRate: 0,
          taxAmt: 0,
          status: 'pending',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };

        const salesDetailRef = doc(collection(db, 'salesDetails'));
        batch.set(salesDetailRef, salesDetail);
        recordsCreated++;

        // Process in batches of 500 to avoid Firestore limits
        if (recordsCreated % 500 === 0) {
          console.log(`💾 Saving batch of 500 records... (${recordsCreated} total so far)`);
          await batch.commit();
          // Create new batch for next set
          const newBatch = writeBatch(db);
          Object.assign(batch, newBatch);
        }
      }
    }

    // Commit any remaining records
    if (recordsCreated % 500 !== 0) {
      console.log(`💾 Saving final batch of ${recordsCreated % 500} records...`);
      await batch.commit();
    }

    console.log(`✅ Successfully created ${recordsCreated} sales detail records`);

    return {
      success: true,
      message: `Successfully seeded ${recordsCreated} sales detail records for ${forDate}`,
      recordsCreated
    };

  } catch (error) {
    console.error('Error seeding daily sales details:', error);
    return {
      success: false,
      message: `Error seeding sales details: ${error}`,
      recordsCreated: 0
    };
  }
};

// Get sales details for an outlet on a specific date
export const getSalesDetailsForOutlet = async (
  outletId: string, 
  date: string,
  warehousePricing?: { [productId: string]: number }
): Promise<SalesDetail[]> => {
  try {
    console.log(`🔍 Loading sales details for outlet: ${outletId}, date: ${date}`);
    
    const q = query(
      collection(db, 'salesDetails'),
      where('outletId', '==', outletId),
      where('date', '==', date)
      // Removed orderBy to avoid composite index requirement
    );
    
    const snapshot = await getDocs(q);
    console.log(`📊 Found ${snapshot.docs.length} seeded sales detail records`);
    
    if (snapshot.docs.length > 0) {
      // Use seeded records if they exist - update unitRate from warehouse pricing
      const salesDetails = snapshot.docs.map(doc => {
        const data = doc.data() as SalesDetail;
        const productId = data.productId;
        // Use warehouse pricing if available, otherwise keep existing rate
        const unitRate = warehousePricing?.[productId] ?? data.unitRate;
        
        return {
          ...data,
          id: doc.id, // Override the id with Firestore document ID
          unitRate, // Update with warehouse pricing
          lineTotal: data.saleQty * unitRate // Recalculate line total
        } as SalesDetail;
      });
      
      return salesDetails.sort((a, b) => a.productName.localeCompare(b.productName));
    }

    // FALLBACK: No seeded records found, generate default records from active products
    console.log(`⚠️ No seeded records found for outlet ${outletId}. Generating fallback records...`);
    
    const productsQuery = query(
      collection(db, 'products'),
      where('isActive', '==', true)
    );
    const productsSnapshot = await getDocs(productsQuery);
    console.log(`📦 Found ${productsSnapshot.docs.length} active products for fallback records`);

    if (productsSnapshot.docs.length === 0) {
      console.log(`❌ No active products found. Cannot generate fallback records.`);
      return [];
    }

    // Generate default sales detail records
    const fallbackRecords: SalesDetail[] = productsSnapshot.docs.map(productDoc => {
      const product = { id: productDoc.id, ...productDoc.data() } as any;
      
      // 🔧 FIX: Use standardized productId field instead of document ID for fallback records too
      const productId = product.productId || product.id;
      const productName = product.name || product.productName || `Product ${productId}`;
      // Use warehouse pricing if available, otherwise use 0 (NO FALLBACK as per requirement)
      const unitRate = warehousePricing?.[productId] || 0;
      
      return {
        id: `fallback-${outletId}-${productId}`, // Temporary ID for fallback records - use productId
        transactionDetailId: 0, // Will be set when transaction is saved
        productId: productId, // ✅ Fixed: Use productId field for consistency
        productName: productName,
        outletId: outletId,
        date: date,
        shelfQtyReco: 0, // No target - salesman determines need manually
        openingStock: 0, // To be entered by salesman
        replaceQty: 0, // To be entered by salesman
        saleQty: 0, // To be entered by salesman (manual since no target)
        unitRate: unitRate, // Use warehouse pricing or 0
        lineTotal: 0,
        taxRate: 0,
        taxAmt: 0,
        status: 'pending' as const,
        // Note: createdAt/updatedAt will be set when transaction is saved
      };
    });

    console.log(`✅ Generated ${fallbackRecords.length} fallback records`);
    return fallbackRecords.sort((a, b) => a.productName.localeCompare(b.productName));
    
  } catch (error) {
    console.error('Error in getSalesDetailsForOutlet:', error);
    throw error; // Re-throw to let the calling function handle it
  }
};

// 🛡️ Check vehicle state before allowing transactions - CRITICAL STATE GUARD
export const checkVehicleStateForTransaction = async (
  salesmanId: string,
  date: string
): Promise<{ canTransact: boolean; message: string; vehicleState?: string; routeId?: string }> => {
  try {
    console.log(`🛡️ Checking route state for salesman ${salesmanId} on ${date}...`);
    
    // Step 1: Find the salesman's route assignment for this date
    const beatsQuery = query(
      collection(db, 'beats'),
      where('salesmanId', '==', salesmanId),
      where('forDate', '==', date),
      where('isActive', '==', true)
    );
    
    const beatsSnapshot = await getDocs(beatsQuery);
    if (beatsSnapshot.empty) {
      return {
        canTransact: false,
        message: 'No vehicle assignment found for today. Please contact your supervisor.',
        vehicleState: 'no_assignment'
      };
    }
    
    const beat = beatsSnapshot.docs[0].data();
    const routeId = beat.routeId; // custom routeId like "RT002"
    
    if (!routeId) {
      return {
        canTransact: false,
        message: 'No route assigned for today. Please contact your supervisor.',
        vehicleState: 'no_route'
      };
    }
    
    // Step 2: Check route status from route_daily_status
    const statusQuery = query(
      collection(db, 'route_daily_status'),
      where('routeId', '==', routeId),
      where('date', '==', date)
    );
    
    const statusSnapshot = await getDocs(statusQuery);
    if (statusSnapshot.empty) {
      return {
        canTransact: false,
        message: 'No loadout planned for your route today. Please contact the warehouse team.',
        vehicleState: 'planning',
        routeId
      };
    }
    
    const routeStatus = statusSnapshot.docs[0].data();
    const currentState = routeStatus.status;
    console.log(`🛡️ Route ${routeId} current state: ${currentState}`);
    
    // Step 4: State-based transaction permissions
    switch (currentState) {
      case 'planning':
        return {
          canTransact: false,
          message: 'Your loadout is still being planned by the warehouse team. Please wait for confirmation.',
          vehicleState: currentState
        };
        
      case 'issuing':
        return {
          canTransact: false,
          message: 'Your loadout has been issued but not confirmed yet. Please confirm your loadout in the Inventory Management section before starting sales.',
          vehicleState: currentState
        };
        
      case 'on_field':
        return {
          canTransact: true,
          message: 'Route is ready for sales transactions.',
          vehicleState: currentState,
          routeId
        };
        
      case 'settling':
        return {
          canTransact: false,
          message: 'Your day is being settled. No new transactions allowed.',
          vehicleState: currentState
        };
        
      case 'settled':
        return {
          canTransact: false,
          message: 'Your day has been settled. No new transactions allowed.',
          vehicleState: currentState
        };
        
      default:
        return {
          canTransact: false,
          message: `Unknown route state: ${currentState}. Please contact your supervisor.`,
          vehicleState: currentState,
          routeId
        };
    }
    
  } catch (error) {
    console.error('Error checking vehicle state for transaction:', error);
    return {
      canTransact: false,
      message: 'Unable to verify route status. Please try again or contact support.',
      vehicleState: 'error'
    };
  }
};

// Get salesman's route from beat assignment
export const getSalesmanRouteForDate = async (
  salesmanId: string,
  date: string
): Promise<string> => {
  try {
    console.log(`🔍 Looking for ${salesmanId}'s route assignment for ${date}...`);
    
    const beatsQuery = query(
      collection(db, 'beats'),
      where('salesmanId', '==', salesmanId),
      where('forDate', '==', date),
      where('isActive', '==', true)
    );
    
    const beatsSnapshot = await getDocs(beatsQuery);
    
    if (beatsSnapshot.docs.length > 0) {
      const beat = beatsSnapshot.docs[0].data();
      console.log(`✅ Found route assignment: ${beat.routeId} (${beat.routeName || 'Unknown Route'})`);
      return beat.routeId;
    }
    
    console.log(`⚠️ No beat assignment found for ${salesmanId} on ${date}. Using default route.`);
    return 'default-route';
    
  } catch (error) {
    console.error('Error getting salesman route:', error);
    return 'default-route';
  }
};

// 🚚 💡 OPTIMIZED: Update vehicle inventory using cached customId
const updateRouteInventoryForSale = async (
  salesmanId: string,
  date: string,
  saleDetail: SalesDetail,
  outletName: string,
  transactionId: string
): Promise<boolean> => {
  try {
    console.log(`🚚 Processing sales inventory transaction (route-based): ${saleDetail.productName} (${saleDetail.saleQty})`);
    // Resolve route for the salesman/date
    const beatsQuery = query(
      collection(db, 'beats'),
      where('salesmanId', '==', salesmanId),
      where('forDate', '==', date),
      where('isActive', '==', true)
    );
    const beatsSnapshot = await getDocs(beatsQuery);
    if (beatsSnapshot.empty) {
      console.warn(`⚠️ No beat found for salesman ${salesmanId} on ${date}.`);
      return false;
    }
    const routeId = beatsSnapshot.docs[0].data().routeId as string;
    if (!routeId) {
      console.warn(`⚠️ Beat missing routeId for salesman ${salesmanId} on ${date}.`);
      return false;
    }
    
  // Step 2: Process sale using unified inventory transaction system
  const totalQuantity = saleDetail.saleQty + saleDetail.replaceQty; // 🔧 FIX: Include both sale and replace quantities
  
  const salesResult = await UnifiedInventoryTransactionService.processSalesTransaction(
    routeId, // Use routeId as the source warehouse
    [{
      productId: saleDetail.productId,
      productName: saleDetail.productName,
      unit: 'KG', // Default unit
      quantity: totalQuantity,
      rate: saleDetail.unitRate
    }],
    saleDetail.outletId, // 🔧 FIX: Pass outletId for journal toWarehouseId
    transactionId,
    salesmanId
  );
    
    if (!salesResult.success) {
      console.error('Failed to process sales inventory:', salesResult.errors);
      return false;
    }
    
  console.log(`✅ Route inventory updated successfully for ${saleDetail.productName}`);
    return true;
    
  } catch (error) {
    console.error('Error updating route inventory for sale:', error);
    return false;
  }
};

// Save a complete sale transaction
export const saveSaleTransaction = async (
  salesDetails: SalesDetail[],
  outletId: string,
  outletName: string,
  salesmanId: string,
  salesmanName: string,
  date: string,
  additionalDiscount: number = 0
): Promise<{ success: boolean; transactionId?: string; message: string }> => {
  try {
    // 🛡️ CRITICAL: Check vehicle state BEFORE starting transaction (to avoid transaction scope issues)
    const vehicleStateCheck = await checkVehicleStateForTransaction(salesmanId, date);
    if (!vehicleStateCheck.canTransact) {
      return {
        success: false,
        message: vehicleStateCheck.message
      };
    }
    
    // Get the salesman's route assignment for this date (also outside transaction)
    const routeId = await getSalesmanRouteForDate(salesmanId, date);
    // Resolve warehouseId from route for grouping (best-effort; optional)
    let warehouseId: string | undefined = undefined;
    try {
      const routesQ = query(
        collection(db, 'routes'),
        where('routeId', '==', routeId)
      );
      const rSnap = await getDocs(routesQ);
      if (!rSnap.empty) {
        const r = rSnap.docs[0].data() as any;
        warehouseId = r.warehouseId;
      }
    } catch {}
    
    return await runTransaction(db, async (transaction) => {
      
      // Generate transaction ID
      const transactionId = await getNextTransactionId();
      const invoiceNo = transactionId; // Already has INV- prefix

  // Calculate totals and txn type
  const netAmt = salesDetails.reduce((sum, detail) => sum + detail.lineTotal, 0);
      const taxAmt = salesDetails.reduce((sum, detail) => sum + detail.taxAmt, 0);
      const total = netAmt + taxAmt;
  const anySale = salesDetails.some(d => d.saleQty > 0);
  const anyReplace = salesDetails.some(d => d.replaceQty > 0);
  const txnType = anySale && anyReplace ? 'sale+replace' : anySale ? 'sale' : anyReplace ? 'replace' : undefined;

      // Get outlet sequence (for now, using a simple counter, can be enhanced)
      const outletSequence = 1; // TODO: Implement proper route sequencing

      // Create sales header
      const salesHeader: Omit<SalesHeader, 'id'> = {
        transactionId,
        routeId,
        warehouseId,
        date,
        outletId,
        outletName,
        txnMode: 'cash', // Default to cash as specified
        txnType,
        outletSequence,
        invoiceNo,
        netAmt,
        taxAmt,
        discount: additionalDiscount,
        total: netAmt + taxAmt - additionalDiscount, // Apply discount to final total
        salesmanId,
        salesmanName,
        status: 'completed',
        // Initialize ERP sync tracking (always set to pending on creation)
        erpSystem: 'tally',
        erpSyncStatus: 'pending',
        erpSyncAttempts: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const salesHeaderRef = doc(collection(db, 'salesHeaders'));
      transaction.set(salesHeaderRef, salesHeader);

      // Handle sales details - both seeded and fallback records
      for (const detail of salesDetails) {
        if (detail.saleQty > 0 || detail.replaceQty > 0) {
          
          // Check if this is a fallback record (temporary ID that doesn't exist in Firebase)
          const isFallbackRecord = detail.id.startsWith('fallback-');
          
          if (isFallbackRecord) {
            // FALLBACK RECORD: Create new document in Firebase
            console.log(`📝 Creating new sales detail record for fallback: ${detail.productName}`);
            
            // Generate proper transaction detail ID for new record
            const transactionDetailId = await getNextTransactionDetailId();
            
            // Create complete sales detail record
            const newSalesDetail: Omit<SalesDetail, 'id'> = {
              transactionDetailId,
              transactionId,
              productId: detail.productId,
              productName: detail.productName,
              outletId: detail.outletId,
              date: detail.date,
              shelfQtyReco: detail.shelfQtyReco,
              openingStock: detail.openingStock,
              replaceQty: detail.replaceQty,
              saleQty: detail.saleQty,
              unitRate: detail.unitRate,
              lineTotal: detail.lineTotal,
              taxRate: detail.taxRate,
              taxAmt: detail.taxAmt,
              status: 'sold',
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            };
            
            // Create new document with auto-generated ID
            const newDetailRef = doc(collection(db, 'salesDetails'));
            transaction.set(newDetailRef, newSalesDetail);
            
          } else {
            // SEEDED RECORD: Update existing document in Firebase
            console.log(`✏️ Updating existing sales detail record: ${detail.productName}`);
            
            const detailRef = doc(db, 'salesDetails', detail.id);
            transaction.update(detailRef, {
              transactionId,
              openingStock: detail.openingStock,
              replaceQty: detail.replaceQty, 
              saleQty: detail.saleQty,
              lineTotal: detail.lineTotal,
              status: 'sold',
              updatedAt: serverTimestamp()
            });
          }

          // �️ IMPLEMENTED: Update route inventory (deduct sold quantities) using unified system
          const inventorySuccess = await updateRouteInventoryForSale(
            salesmanId,
            date,
            detail,
            outletName,
            transactionId
          );
          
          if (!inventorySuccess) {
            throw new Error(`Failed to update vehicle inventory for ${detail.productName}`);
          }
        }
      }

      // � Optional: if needed, update route_daily_status to ensure state remains 'on_field'
      // Skipped here per targeted scope

      return {
        success: true,
        transactionId,
        message: `Sale transaction ${transactionId} saved successfully`
      };
    });

  } catch (error) {
    console.error('Error saving sale transaction:', error);
    return {
      success: false,
      message: `Error saving sale transaction: ${error}`
    };
  }
};

// Cancel a sale transaction
export const cancelSaleTransaction = async (
  transactionId: string
): Promise<{ success: boolean; message: string }> => {
  try {
    return await runTransaction(db, async (transaction) => {
      // Update sales header to cancelled
      const salesHeaderQuery = query(
        collection(db, 'salesHeaders'),
        where('transactionId', '==', transactionId)
      );
      const salesHeaderSnapshot = await getDocs(salesHeaderQuery);
      
      if (salesHeaderSnapshot.empty) {
        throw new Error('Transaction not found');
      }

      const salesHeaderDoc = salesHeaderSnapshot.docs[0];
      transaction.update(salesHeaderDoc.ref, {
        status: 'cancelled',
        updatedAt: serverTimestamp()
      });

      // Revert sales details to pending status
      const salesDetailsQuery = query(
        collection(db, 'salesDetails'),
        where('transactionId', '==', transactionId)
      );
      const salesDetailsSnapshot = await getDocs(salesDetailsQuery);

      salesDetailsSnapshot.docs.forEach(doc => {
        transaction.update(doc.ref, {
          transactionId: null,
          status: 'pending',
          saleQty: 0,
          replaceQty: 0,
          lineTotal: 0,
          updatedAt: serverTimestamp()
        });
      });

      // TODO: Restore vehicle inventory
      // This will be implemented in the next phase

      return {
        success: true,
        message: `Transaction ${transactionId} cancelled successfully`
      };
    });

  } catch (error) {
    console.error('Error cancelling sale transaction:', error);
    return {
      success: false,
      message: `Error cancelling transaction: ${error}`
    };
  }
};

// Get sales summary for a date range
export const getSalesSummary = async (
  startDate: string,
  endDate: string,
  salesmanId?: string
): Promise<{
  totalSales: number;
  totalTransactions: number;
  totalCash: number;
  salesByDate: { [date: string]: number };
}> => {
  try {
    let q = query(
      collection(db, 'salesHeaders'),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      where('status', '==', 'completed')
    );

    if (salesmanId) {
      q = query(q, where('salesmanId', '==', salesmanId));
    }

    const snapshot = await getDocs(q);
    const sales = snapshot.docs.map(doc => doc.data() as SalesHeader);

    const totalSales = sales.reduce((sum, sale) => sum + sale.total, 0);
    const totalTransactions = sales.length;
    const totalCash = sales
      .filter(sale => sale.txnMode === 'cash')
      .reduce((sum, sale) => sum + sale.total, 0);

    const salesByDate: { [date: string]: number } = {};
    sales.forEach(sale => {
      salesByDate[sale.date] = (salesByDate[sale.date] || 0) + sale.total;
    });

    return {
      totalSales,
      totalTransactions,
      totalCash,
      salesByDate
    };

  } catch (error) {
    console.error('Error getting sales summary:', error);
    return {
      totalSales: 0,
      totalTransactions: 0,
      totalCash: 0,
      salesByDate: {}
    };
  }
};
